import { useContext, useEffect, useMemo, useState } from "react";
import { getActiveLanguage, getTranslate } from "react-localize-redux";
import { useSelector } from "react-redux";

import { useGetGameAffiliatesQuery, useGetLocalGameCategoriesQuery } from "@api";
import { MainContext } from "@context";
import { useBettoContainerFilterItems } from "@pages/casino/hooks/useBettoContainerFilterItems";
import { getCategoryLocalizedName, matchesCategoryLiveFilter } from "@pages/casino/utils";
import {
	buildAffiliateProviderRoute,
	persistAffiliateProviderFilter,
} from "@pages/casino/utils/bettoProviderFilter";
import { getUser } from "@utils";

const selectTranslate = (state) => ({
	currency: state.balance.currency,
	translate: getTranslate(state.localize),
	currentLanguage:
		getActiveLanguage(state.localize)?.code ?? localStorage.getItem("lang") ?? "en",
	isContainerLoading: state.thirdParty.casinoContainerLoading,
});

const useController = ({
	onSelectFilter,
	history,
	activeTab,
	filterValues,
	onlySelectFilter,
	showCategoriesFilter,
	showProvidersFilter,
	showContainerCategories,
}) => {
	const [filters, setFilters] = useState([]);
	const [tab, setTab] = useState(activeTab);

	const { translate, currentLanguage, isContainerLoading, currency } =
		useSelector(selectTranslate);
	const { state: contextState } = useContext(MainContext);

	const isLive = useMemo(() => {
		let currentTab = tab?.toLowerCase();

		if (!currentTab) {
			const searchParams = new URLSearchParams(window.location.search);
			currentTab = searchParams.get("tab") ?? searchParams.get("page");
		}

		switch (currentTab) {
			case "casino":
				return false;
			case "livecasino":
				return true;
			default:
				return null;
		}
	}, [tab]);

	const containerPage = tab === "LiveCasino" ? "LiveCasino" : "Casino";
	const { items: containerCategories, isLoading: isBettoContainersLoading } =
		useBettoContainerFilterItems(containerPage, currentLanguage, {
			skip: !showContainerCategories,
		});

	const {
		data: gameCategories,
		isLoading: isGameCategoriesLoading,
		isFetching: isGameCategoriesFetching,
	} = useGetLocalGameCategoriesQuery(
		{
			size: 1000,
			isActive: true,
		},
		{ skip: !showCategoriesFilter }
	);

	const {
		data: gameAffiliates,
		isLoading: isGameAffiliatesLoading,
		isFetching: isGameAffiliatesFetching,
	} = useGetGameAffiliatesQuery({ size: 1000, isLive, currency }, { skip: !showProvidersFilter });

	const categories = useMemo(() => {
		const allCategories = gameCategories?.categories || [];
		const filteredCategories = allCategories.filter((category) =>
			matchesCategoryLiveFilter(category, isLive)
		);
		const visibleCategories =
			filteredCategories.length > 0 ? filteredCategories : allCategories;

		return visibleCategories.map((category) => ({
			key: category.name,
			value: getCategoryLocalizedName(category, currentLanguage),
		}));
	}, [gameCategories?.categories, currentLanguage, isLive]);

	const serializeGameAffiliates = () => {
		return (gameAffiliates || [])
			?.filter((aff) => aff.affiliate !== "egt")
			.map((item) => ({
				key: item.affiliate,
				value: `${item.affiliate} (${item.count})`,
				count: item.count,
			}));
	};

	const handleSelectFilter = (key, value, query) => {
		const isFilterExists = filters?.some((item) => item?.section === key);

		if (!isFilterExists) {
			const newFilters = filters
				? [...filters, { ...value, section: key }]
				: [{ ...value, section: key }];

			setFilters(newFilters);
			onSelectFilter?.(newFilters);
		} else {
			const nextFilters = filters?.map((item) => {
				if (item?.section === key) {
					return { ...value, section: key };
				}
				return item;
			});

			setFilters(nextFilters);
			onSelectFilter?.(nextFilters);
		}

		if (!onlySelectFilter && query) {
			history.push(query);
		}
	};

	const handleAllGamesButtonClick = () => {
		const tabMap = {
			Casino: "casino",
			LiveCasino: "livecasino",
		};
		const query = `/provider?key=allGames&name=all games&tab=${tabMap[activeTab]}`;

		localStorage.removeItem("casino.filters.allGames");
		setFilters([]);
		onSelectFilter?.([]);
		history.push(query);
	};

	const handleClearProviderFilter = () => {
		const tabMap = {
			Casino: "casino",
			LiveCasino: "livecasino",
		};
		const nextFilters = filters?.filter((item) => item?.section !== "provider") ?? [];

		setFilters(nextFilters);
		onSelectFilter?.(nextFilters);
		localStorage.removeItem("casino.filters.affiliate");
		localStorage.removeItem("casino.filters.allGames");

		if (window.TENANT === "betto") {
			history.push(`/casino?page=${tabMap[activeTab]}`);
		} else {
			history.push(`/provider?key=allGames&name=all games&tab=${tabMap[activeTab]}`);
		}
	};

	const handleProviderSelect = (provider) => {
		if (!provider) {
			handleClearProviderFilter();
			return;
		}

		const tabSlug = activeTab?.toLowerCase() === "livecasino" ? "livecasino" : "casino";
		persistAffiliateProviderFilter(provider);
		handleSelectFilter("provider", provider, buildAffiliateProviderRoute(provider, tabSlug));
	};

	useEffect(() => {
		if (!filterValues?.length || !gameCategories?.categories?.length) {
			setFilters(filterValues);
			return;
		}

		const updatedFilters = filterValues.map((filter) => {
			if (filter?.section !== "category") {
				return filter;
			}

			const category = gameCategories.categories.find((item) => item.name === filter.key);
			if (!category) {
				return filter;
			}

			return {
				...filter,
				value: getCategoryLocalizedName(category, currentLanguage),
			};
		});

		setFilters(updatedFilters);
	}, [filterValues, gameCategories, currentLanguage]);

	useEffect(() => {
		setTab(activeTab);
	}, [activeTab]);

	return {
		helpers: { translate },
		state: {
			...contextState,
			filters,
			user: getUser(),
			categories,
			containerCategories,
			affiliates: serializeGameAffiliates(),
			isContainerLoading: isContainerLoading || isBettoContainersLoading,
			isGameCategoriesLoading: isGameCategoriesFetching || isGameCategoriesLoading,
			isGameAffiliatesLoading: isGameAffiliatesFetching || isGameAffiliatesLoading,
		},
		handlers: {
			handleSelectFilter,
			handleAllGamesButtonClick,
			handleClearProviderFilter,
			handleProviderSelect,
		},
	};
};

export default useController;
